"use client";

import React from "react";
import BaseTailoringQuestions, { TailoringQuestion } from "../../shared/BaseTailoringQuestions";

interface OpeningBalanceTestingTailoringQuestionsProps {
  onBack?: () => void;
}

const tailoringQuestions: TailoringQuestion[] = [
  {
    id: "OBT-1",
    question: "Are there any opening balances for this first year audit?",
    type: "radio",
    options: ["Yes", "No"],
  },
  {
    id: "OBT-2",
    question: "Have the financial statements for the prior period been audited?",
    type: "radio",
    options: ["Yes", "No"],
    condition: {
      questionId: "OBT-1",
      value: "Yes",
    },
  },
  {
    id: "OBT-3",
    question: "Have we obtained the most recent financial statements and the predecessor auditor's report thereon, for information relevant to opening balances, including disclosures?",
    type: "radio",
    options: ["Yes", "No"],
    condition: {
      questionId: "OBT-2",
      value: "Yes",
    },
  },
  {
    id: "OBT-4",
    question: "Was the predecessor auditor's opinion unmodified?",
    type: "radio",
    options: ["Yes", "No"],
    // Only show if OBT-2 = Yes AND OBT-3 = Yes
    // Since we can only check one condition, we check OBT-3 = Yes (which already requires OBT-2 = Yes)
    condition: {
      questionId: "OBT-3",
      value: "Yes",
    },
  },
  {
    id: "OBT-5",
    question: "Does the modification remain relevant and material to the current period's financial statements?",
    type: "radio",
    options: ["Yes", "No"],
    // Only show if OBT-2 = Yes AND OBT-3 = Yes AND OBT-4 = No
    // We check OBT-4 = No (which requires OBT-3 = Yes, which requires OBT-2 = Yes)
    condition: {
      questionId: "OBT-4",
      value: "No",
    },
    follow_up: {
      condition: "Yes",
      id: "OBT-5-note",
      question: "Note: The auditor shall modify the auditor's opinion on the current period's financial statements in accordance with SA 705 and SA 710.",
      type: "text",
      options: [],
    },
  },
  // These questions show if OBT-3 is No OR if OBT-4 is Yes (unmodified)
  // OBT-6 and OBT-7 should show when:
  // - OBT-2 = No (prior period not audited), OR
  // - OBT-3 = No (didn't obtain statements/report), OR
  // - OBT-4 = Yes (unmodified opinion)
  // We'll show them based on the most specific condition that can be checked
  // First check: If OBT-2 = No, show them
  {
    id: "OBT-6",
    question: "Have the prior-period's closing balances been correctly brought forward to current period, or when appropriate, have they been restated?",
    type: "radio",
    options: ["Yes", "No"],
    condition: {
      questionId: "OBT-2",
      value: "No",
    },
  },
  {
    id: "OBT-7",
    question: "Determine whether the opening balances reflect the application of appropriate accounting policies?",
    type: "radio",
    options: ["Yes", "No"],
    condition: {
      questionId: "OBT-2",
      value: "No",
    },
  },
  // Second check: If OBT-2 = Yes but OBT-3 = No, show them
  {
    id: "OBT-6-v2",
    question: "Have the prior-period's closing balances been correctly brought forward to current period, or when appropriate, have they been restated?",
    type: "radio",
    options: ["Yes", "No"],
    condition: {
      questionId: "OBT-3",
      value: "No",
    },
  },
  {
    id: "OBT-7-v2",
    question: "Determine whether the opening balances reflect the application of appropriate accounting policies?",
    type: "radio",
    options: ["Yes", "No"],
    condition: {
      questionId: "OBT-3",
      value: "No",
    },
  },
  // Third check: If OBT-3 = Yes and OBT-4 = Yes (unmodified), show them
  {
    id: "OBT-6-v3",
    question: "Have the prior-period's closing balances been correctly brought forward to current period, or when appropriate, have they been restated?",
    type: "radio",
    options: ["Yes", "No"],
    condition: {
      questionId: "OBT-4",
      value: "Yes",
    },
  },
  {
    id: "OBT-7-v3",
    question: "Determine whether the opening balances reflect the application of appropriate accounting policies?",
    type: "radio",
    options: ["Yes", "No"],
    condition: {
      questionId: "OBT-4",
      value: "Yes",
    },
  },
];

export default function OpeningBalanceTestingTailoringQuestions({
  onBack,
}: OpeningBalanceTestingTailoringQuestionsProps) {
  return (
    <BaseTailoringQuestions
      areaName="Opening Balance Testing"
      title="Opening Balance Testing - Tailoring Questions"
      description="Answer the following questions to customize the audit program for Opening Balance Testing."
      questions={tailoringQuestions}
      onBack={onBack}
    />
  );
}
